import {
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { EMPTY, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  map,
  mergeMap,
  takeUntil,
  throttleTime,
} from 'rxjs/operators';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { StoreState } from '../core/states/store.state';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import {
  GetCountries,
  GetLanguages,
  GetPlans,
  GetStores,
  GetTemplates,
  StoreLoading,
  StoreNotLoading,
} from '../core/states/store.actions';
import {
  DomainPatternValidator,
  EmailPatternValidator,
  StoreNamePatternValidator,
  SubDomainPatternValidator,
} from '../core/constants/custom-validators.constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from '../store.service';
import { environment } from '../../environments/environment';
import { DealerService } from '../dealer.service';
import { DataService } from '../data.service';
import { UtilitiesService } from '../core/utilities.service';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit, OnDestroy {
  @ViewChild('customDomainContent') customDomainLoader;
  @ViewChild('restrictedWordsModalContent') restrictedWordsModalContent;
  @ViewChild('footerContent') footerContent;
  @ViewChild('templateContent') templateContent;

  defaultImg =
    'https://via.placeholder.com/283x200.jpg/747e8b/ffffff?text=Template+image';
  paidSelected = false;

  freePlanCode = 'FIV5S_STARTER';
  maxFreeStore = 1;

  storeSubdomain = environment.storeSubdomain;

  state: any = {
    step: 1,
    emailRequired: false,
    customDomain: '',
    submitting: false,
    allowFreeStore: true,
  };

  storeForm = this.fb.group({
    email: ['', [Validators.required, EmailPatternValidator]],
    name: [
      '',
      [StoreNamePatternValidator, Validators.required, Validators.minLength(5)],
    ],
    country: ['', [Validators.required, Validators.minLength(2)]],
    language: ['', [Validators.required, Validators.minLength(2)]],
    template: ['', [Validators.required]],
    plan: ['', [Validators.required]],
    useCustomDomain: false,
    subdomainName: [
      '',
      [
        Validators.required,
        SubDomainPatternValidator,
        Validators.minLength(5),
        Validators.maxLength(24),
      ],
    ],
    customDomain: [
      '',
      [
        DomainPatternValidator,
        Validators.minLength(5),
        Validators.maxLength(64),
      ],
    ],
  });

  maxCatDisplay = 12;
  viewAllCategories = false;
  isMobileView = false;

  languageList = [];
  countryList = [];
  templateFilters = [];
  selectedTemplateFilter;
  restrictedWords = [];
  restrictedWordsLoaded = false;

  debouncedScrollEvent: Observable<any>;
  debouncedScrollSubscription: Subscription;
  throttledScrollEvent: Observable<any>;
  throttledScrollSubscription: Subscription;
  backToTopIsActive: boolean = false;
  backToTopIsAbsolute: boolean = false;
  validatingSubDomain: boolean = false;

  dealerCredits = 0;
  dealerMinimumCredit = 10;
  dealerCreditsValid = false;
  dealerCreditsLoaded = false;
  dealerCreditsLoading = false;

  aiGenerateStoreContent = false;
  private aiGenerateStoreContentTouched = false;
  aiGenerateStoreContentFeatureEnabled = true;

  aiContentLanguageCodes: string[] = [];

  private lastAutoSubdomainName = '';
  private lastAutoSubdomainCandidate = '';
  private subdomainManuallyEdited = false;

  destroy$: Subject<boolean> = new Subject<boolean>();
  @Select(StoreState.countries) countries$: Observable<any[]>;
  @Select(StoreState.languages) languages$: Observable<any[]>;
  @Select(StoreState.plans) plans$: Observable<any[]>;
  @Select(StoreState.templates) templates$: Observable<any[]>;

  constructor(
    private store: Store,
    public router: Router,
    private fb: FormBuilder,
    private svc: StoreService,
    private modalService: NgbModal,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private dealerService: DealerService,
    private dataService: DataService,
    private utils: UtilitiesService,
  ) {
    if (!window) {
      return;
    }

    ngZone.runOutsideAngular(() => {
      this.debouncedScrollEvent = fromEvent(window, 'scroll').pipe(
        debounceTime(100),
      );

      this.throttledScrollEvent = fromEvent(window, 'scroll').pipe(
        throttleTime(100),
      );

      this.throttledScrollSubscription = this.throttledScrollEvent.subscribe(
        (e) => {
          this.doScroll();
        },
      );

      this.debouncedScrollSubscription = this.debouncedScrollEvent.subscribe(
        (e) => {
          this.doScroll();
        },
      );
    });
  }

  /**
   * Single source of truth for whether AI content generation is actually on.
   * The feature flag must be ANDed in here so the toggle state, the disabled
   * state and the outgoing request can never disagree with each other.
   */
  get aiGenerateContentEnabled(): boolean {
    return this.aiGenerateStoreContentFeatureEnabled && this.aiGenerateStoreContent;
  }

  onAiGenerateStoreContentChange(checked: boolean) {
    this.aiGenerateStoreContentTouched = true;
    this.aiGenerateStoreContent = !!checked;
    if (this.aiGenerateStoreContent) {
      this.ensureDefaultLanguageSelected();
    }
  }

  get defaultAiLanguageCode(): string {
    const formCode = this.language?.value?.language_code;
    if (formCode) return formCode;
    const first = this.languageList?.[0]?.language_code;
    return first || '';
  }

  isDefaultAiLanguage(code: string): boolean {
    return !!code && code === this.defaultAiLanguageCode;
  }

  isAiLanguageSelected(code: string): boolean {
    return this.aiContentLanguageCodes.indexOf(code) !== -1;
  }

  toggleAiLanguage(code: string) {
    if (!code || this.isDefaultAiLanguage(code)) {
      return;
    }
    const idx = this.aiContentLanguageCodes.indexOf(code);
    if (idx === -1) {
      this.aiContentLanguageCodes = [...this.aiContentLanguageCodes, code];
    } else {
      this.aiContentLanguageCodes = this.aiContentLanguageCodes.filter(
        (c) => c !== code
      );
    }
  }

  get aiGenerationEstimatedTime(): { min: number; max: number } {
    const baseMin = 5;
    const baseMax = 10;
    const perExtra = 3;
    const count = Math.max(0, (this.aiContentLanguageCodes?.length || 1) - 1);
    return {
      min: baseMin + count * perExtra,
      max: baseMax + count * perExtra,
    };
  }

  private ensureDefaultLanguageSelected() {
    const def = this.defaultAiLanguageCode;
    if (def && this.aiContentLanguageCodes.indexOf(def) === -1) {
      this.aiContentLanguageCodes = [def, ...this.aiContentLanguageCodes];
    }
  }

  onAiGenerateCardClick(event?: MouseEvent) {
    event?.preventDefault();

    const isDisabled =
      !this.aiGenerateStoreContentFeatureEnabled ||
      !this.dealerCreditsLoaded ||
      this.dealerCreditsLoading ||
      this.dealerCredits < this.dealerMinimumCredit ||
      !this.dealerCreditsValid;

    if (isDisabled) {
      return;
    }

    this.onAiGenerateStoreContentChange(!this.aiGenerateStoreContent);
  }

  private async refreshDealerCredits() {
    if (this.dealerCreditsLoading) {
      return;
    }

    this.dealerCreditsLoading = true;
    try {
      const res: any = await this.dealerService.fetchDealerInfo();
      const credits = res?.credits ?? res?.dealer_credits ?? res?.dealerCredits;
      const totalCredit =
        +(
          credits?.totalCredit ??
          credits?.total_credit ??
          credits?.total ??
          credits
        ) || 0;
      const minimumCredit =
        +(
          credits?.minimumCredit ??
          credits?.minimum_credit ??
          credits?.minimum
        ) || 10;
      const isValidRaw = credits?.is_valid ?? credits?.isValid;
      const isValid = typeof isValidRaw === 'undefined' ? true : !!isValidRaw;

      this.dealerCredits = totalCredit;
      this.dealerMinimumCredit = minimumCredit;
      this.dealerCreditsValid = isValid;
      this.dataService.updateDealerCredits(totalCredit);

      if (!this.aiGenerateStoreContentTouched) {
        this.aiGenerateStoreContent = totalCredit >= minimumCredit;
      }

      if (this.aiGenerateStoreContent) {
        this.ensureDefaultLanguageSelected();
      }

      this.dealerCreditsLoaded = true;
    } finally {
      this.dealerCreditsLoading = false;
    }
  }

  ngOnInit(): void {
    const stores = this.store.selectSnapshot(StoreState.storesList);

    /*
    // Only allow 1 store
    if (stores.length) {
      this.router.navigate(['/dashboard']);
    }
    */

    this.state.allowFreeStore =
      stores.filter((store) => store.store_plan === this.freePlanCode).length <
      this.maxFreeStore;

    this.store
      .dispatch([
        new StoreLoading(),
        //new GetTemplates(),
        //new GetPlans(),
        new GetCountries(),
        new GetLanguages(),
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => {
        const plans = this.store.selectSnapshot(StoreState.plans);
        const templates = this.store.selectSnapshot(StoreState.templates);
        const userInfo = this.store.selectSnapshot(StoreState.userInfo);
        const countries = this.store.selectSnapshot(StoreState.countries);
        const languages = this.store.selectSnapshot(StoreState.languages);

        //console.log(userInfo);

        if (countries.length > 0) {
          //this.country.patchValue(countries[5]);
        }

        if (plans.length > 0 && templates.length >= 0) {
          this.form.patchValue({
            plan: plans[+!this.state.allowFreeStore],
          });

          this.paidSelected = this.storePlan.value.id != 1;

          this.store.dispatch(new StoreNotLoading());
          if (userInfo && userInfo.profile && userInfo.profile.profile_email) {
            this.email.patchValue(userInfo.profile.profile_email);
            this.state.step = 1;
          }

          // const filters = ['All',...new Set([].concat.apply([], templates.map(item => item.tags)))];
          const filters = [
            'All',
            ...new Set(templates.map((item) => item.category)),
          ];
          this.templateFilters = filters.map((item) => ({
            display: item,
            value: item,
          }));

          // this.selectedTemplateFilter = this.templateFilters[0];
          this.updateTemplateDisplay(this.templateFilters[0]);
        }
      });

    this.subdomainName.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: any) => {
        const current = (val ?? '').toString();
        if (current.trim() === '') {
          this.subdomainManuallyEdited = false;
          return;
        }

        if (
          this.subdomainName.dirty &&
          current !== this.lastAutoSubdomainName
        ) {
          this.subdomainManuallyEdited = true;
        }
      });

    this.name.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: any) => {
        const suggested = this.formatSubdomainFromStoreName(
          (val ?? '').toString(),
        );
        this.lastAutoSubdomainCandidate = suggested;
      });

    this.useCustomDomain.valueChanges.subscribe((val) => {
      const { subdomainName, customDomain } = this.form.controls;
      customDomain.patchValue('');
      if (val) {
        // if(this.storePlan.value && this.storePlan.value.id == 1) {
        //   const plans = this.store.selectSnapshot(StoreState.plans);
        //   this.form.patchValue({
        //     plan:plans[1]
        //   });
        // }

        // subdomainName.markAsUntouched();
        customDomain.setValidators([
          Validators.required,
          DomainPatternValidator,
        ]);
      } else {
        customDomain.markAsUntouched();
        customDomain.clearValidators();
      }
      // subdomainName.updateValueAndValidity();
      customDomain.updateValueAndValidity();
    });

    this.storePlan.valueChanges.subscribe((val) => {
      this.storeTemplate.patchValue('');
      if (val && val.id == 1) {
        // Free package is selected
        this.useCustomDomain.patchValue(false);
        this.customDomain.patchValue('');
        // this.useCustomDomain.disable();
        this.paidSelected = false;
      } else {
        this.useCustomDomain.enable();
        this.paidSelected = true;
      }
    });

    this.checkWidth(null);

    const token = localStorage.getItem('bearer');
    this.svc.getCountryLists(token).subscribe((res) => {
      this.countryList = res['data'];
    });
    this.svc.getLanguageLists(token).subscribe((res) => {
      this.languageList = res['data'];
    });
  }

  private formatSubdomainFromStoreName(name: string): string {
    const raw = (name ?? '').toString().trim().toLowerCase();
    if (!raw) {
      return '';
    }

    const ascii = raw.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

    let slug = ascii
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');

    slug = slug.slice(0, 24).replace(/-+$/, '').replace(/^-+/, '');
    return slug;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    // Unsubscribe from the subject
    this.destroy$.unsubscribe();
    // this.debouncedScrollEvent.unsubscribe();
    this.debouncedScrollSubscription.unsubscribe();
    // this.throttledScrollEvent.unsubscribe();
    this.throttledScrollSubscription &&
      this.throttledScrollSubscription.unsubscribe();
  }

  ngOnChanges(): void {}

  @HostListener('window:resize', ['$event'])
  checkWidth(event) {
    const innerWidth = window.innerWidth;
    this.isMobileView = innerWidth < 1200;
  }

  gotoStep(num: number) {
    let isValid = true;

    if (this.state.step < num) {
      switch (this.state.step) {
        case 1:
          isValid = this.name.valid;
          break;
        case 2:
          isValid = this.canValidateDomain;
          break;
        case 3:
          isValid = this.storeTemplate.valid;
          break;
      }
    }

    if (isValid) {
      this.state.step = num;
      this.focusStepInput();
      if (this.state.step === 5) {
        this.refreshDealerCredits();
      }
    }
  }

  nextStep(control?: AbstractControl) {
    if (control === undefined || (control && control.valid)) {
      this.state.step++;

      // temporary skip step 2
      if (this.state.step == 2) {
        this.state.step = 3;
      }

      // temporary skip step 4
      if (this.state.step == 4) {
        this.state.step = 5;
      }

      this.focusStepInput();
      if (this.state.step === 5) {
        this.refreshDealerCredits();
      }
    } else {
      control.markAsTouched();
    }

    //console.log('Step changed', this.state.step)
    //if (this.state.step == 2) {
    // this.store.dispatch(new GetStores(null));
    // const { allowFreeStore } = this.state;
    // console.log({ allowFreeStore })
    //}
  }

  prevStep() {
    this.state.step--;

    // temporary skip step 4
    if (this.state.step == 4) {
      this.state.step = 3;
    }

    // temporary skip step 2
    if (this.state.step == 2) {
      this.state.step = 1;
    }

    this.focusStepInput();
  }

  nextStepOnEnter(e: any) {
    console.log('current step', this.state.step);
    if (this.state.step == 3) {
      this.validateDomain();
      return;
      // } else if (this.state.step == 2) {
      // this.gotoStep(4)
      // return;
      // } else if (this.state.step == 1) {
      this.nextStep(this.name);
      // this.gotoStep(3)
      // return;
    }
    this.submitEmail();
  }

  createNewStore() {
    const params = {
      countryCode: this.country.value.country_code,
      languageCode: this.language.value.language_code,
      store_name: this.name.value,
      store_plan_id: this.storePlan.value.id || 2,
      store_subdomain: this.subdomainName.value,
      theme_id: this.storeTemplate.value.id || 1,
      generate_ai_content: this.aiGenerateContentEnabled,
      ai_content_language_codes: this.aiGenerateContentEnabled
        ? this.aiContentLanguageCodes
        : [],
    };

    this.modalService.open(this.customDomainLoader, {
      centered: true,
      backdrop: 'static',
    });

    // let newStoreInfo = null;
    const token = localStorage.getItem('bearer');
    this.svc
      .createStore(token, params)
      .pipe(
        // mergeMap(
        map((res) => {
          if (res && res['status'] == 'success' && res['status_code'] == 200) {
            setTimeout(() => {
              const store_id = res['data']['store_id'];
              localStorage.setItem('default_store', store_id);
              localStorage.setItem('store_id', store_id);
              const location = `${window.location.origin}${
                window.location.hash.indexOf('#') !== -1 ? '/#' : ''
              }`;
              window.location.href = `${location}/dashboard/overview`; //res.data.url;
              this.modalService.dismissAll();
            }, 0);
          }
        }),
      )
      .subscribe(
        (_) => _,
        (err) => {
          this.modalService.dismissAll();

          const apiError = err?.error ?? err;
          const rawMessage = apiError?.message ?? apiError?.error?.message;
          const rawData = apiError?.data ?? apiError?.error?.data;

          const asObject =
            rawMessage && typeof rawMessage === 'object' ? rawMessage : null;
          const dataObject =
            rawData && typeof rawData === 'object' ? rawData : null;

          const message =
            typeof rawMessage === 'string'
              ? rawMessage
              : typeof rawData === 'string'
                ? rawData
                : asObject || dataObject
                  ? 'Please review the following issues:'
                  : 'Sorry, but we have encountered an issue, please try again later.';

          const detailsObject = asObject || dataObject;
          const detailsHtml = detailsObject
            ? `<ul class=\"mt-2 mb-0 pl-3\">${Object.keys(detailsObject)
                .map((key) => {
                  const val = (detailsObject as any)[key];
                  const text = Array.isArray(val) ? val.join(', ') : `${val}`;
                  return `<li><strong>${key}:</strong> ${text}</li>`;
                })
                .join('')}</ul>`
            : '';
          const refId = apiError?.refId ?? apiError?.error?.refId;

          const refIdText = refId
            ? `<div class=\"mt-2\"><strong>Reference ID:</strong> ${refId}</div>`
            : '';

          this.utils.msgBox?.open({
            title: 'Error',
            icon: 'error',
            message: `<div>${message}</div>${detailsHtml}${refIdText}`,
          });
        },
      );
  }

  submitEmail() {
    const { email } = this.form.controls;

    console.log('submitting email...');
    if (email.valid) {
      this.modalService.open(this.customDomainLoader, {
        centered: true,
        backdrop: 'static',
      });

      const params = {
        email: email.value,
      };

      email.markAsTouched();
      this.svc.validateEmailAddress(params).subscribe(
        (res) => {
          if (res && res.code == 'OK') {
            this.modalService.dismissAll();

            if (res.status === true) {
              this.nextStep();
            } else {
              email.setErrors({ taken: true });
            }
          }
        },
        (err) => {
          console.log(err);
          email.setErrors({ taken: err });
          this.modalService.dismissAll();
        },
      );
    }
  }

  focusStepInput() {
    console.log(this.state.step);
    this.focusElement(`.main-input-${this.state.step}`);
  }

  focusElement(querySelector: string): void {
    setTimeout(
      () => (document.querySelector(querySelector) as any)?.focus(),
      1,
    );
  }

  updatePlan(plan) {
    this.form.patchValue({ plan: plan });
  }

  updateTemplate(template) {
    this.form.patchValue({
      template: template.is_available == '1' ? template : '',
    });
  }

  validateStoreInfo() {
    const { name, country, language } = this.form.controls;

    if (!country.value.country_name || !language.value.language_name) {
      if (!country.value.country_name) {
        country.setErrors({ required: true });
        country.markAsTouched();
      }
      if (!language.value.language_name) {
        language.setErrors({ required: true });
        language.markAsTouched();
      }
    } else {
      const candidate = this.formatSubdomainFromStoreName(
        (name.value ?? '').toString(),
      );

      if (!candidate) {
        this.gotoStep(3);
        return;
      }

      this.modalService.open(this.customDomainLoader, {
        centered: true,
        backdrop: 'static',
      });

      const token = localStorage.getItem('bearer');
      this.svc
        .suggestAvailableSubdomain(token, { subdomain: candidate })
        .subscribe(
          (res) => {
            if (
              res &&
              res['status'] === 'success' &&
              res['status_code'] === 'OK'
            ) {
              const data = res['data'];
              if (Array.isArray(data) && data.length > 0) {
                const suggestion = (data[0] ?? '').toString();
                if (suggestion) {
                  const current = (this.subdomainName.value ?? '').toString();
                  if (!this.subdomainManuallyEdited) {
                    if (
                      current.trim() === '' ||
                      current === candidate ||
                      current === this.lastAutoSubdomainName
                    ) {
                      this.subdomainName.patchValue(suggestion, {
                        emitEvent: false,
                      });
                      this.lastAutoSubdomainName = suggestion;
                      this.subdomainManuallyEdited = false;
                    }
                  }
                }
              }
            }

            this.modalService.dismissAll();
            this.gotoStep(3);
          },
          (_err) => {
            this.modalService.dismissAll();
            this.gotoStep(3);
          },
        );
    }
  }

  validateDomain() {
    const { subdomainName, useCustomDomain, customDomain } = this.form.controls;

    if (subdomainName.value.trim() == '') {
      this.nextStep(subdomainName);
    } else {
      this.validatingSubDomain = true;
      this.modalService.open(this.customDomainLoader, {
        centered: true,
        backdrop: 'static',
      });

      const params1 = {
        subDomain: subdomainName.value,
      };
      const token = localStorage.getItem('bearer');
      this.svc
        .validateSubDomain(token, params1)
        .pipe(
          mergeMap((res) => {
            if (
              res &&
              res['status'] == 'success' &&
              res['status_code'] == 200
            ) {
              console.log('res', { res });
              if (res['data'].available !== true) {
                subdomainName.setErrors({ taken: true });
                subdomainName.markAsTouched();
              }
            }

            if (useCustomDomain.value && customDomain.valid) {
              this.validatingSubDomain = false;
              const params2 = {
                domain: customDomain.value,
              };

              customDomain.markAsTouched();
              return this.svc.validateDomain(token, params2);
            } else {
              this.modalService.dismissAll();
              if (subdomainName.valid) {
                this.nextStep();
              }
              return EMPTY;
            }
          }),
        )
        .subscribe(
          (res) => {
            if (
              res &&
              res['status'] == 'success' &&
              res['status_code'] == 200
            ) {
              if (res['data'].available == true) {
                if (subdomainName.valid) {
                  this.nextStep();
                }
              } else {
                customDomain.setErrors({ taken: true });
                this.nextStep(customDomain);
              }

              setTimeout(() => {
                this.modalService.dismissAll();
              }, 200);
            }
          },
          (err) => {
            subdomainName.setErrors({ error_msg: err.error?.message });
            customDomain.setErrors({ pattern: true });
            this.modalService.dismissAll();
          },
        );
    }
  }

  showRestrictedWordModal() {
    if (!this.restrictedWordsLoaded) {
      this.svc.getRestrictedSubdomain().subscribe((res) => {
        this.restrictedWordsLoaded = true;

        this.restrictedWords = res?.data
          .map((item) => {
            return item?.reserved_subdomain;
          })
          .sort();
      });
    }

    this.modalService.open(this.restrictedWordsModalContent, {
      centered: true,
      windowClass: 'restricted-modal-window',
      backdrop: 'static',
    });
  }

  validateTemplate() {
    this.nextStep();
  }

  loadAllCategories() {
    this.maxCatDisplay += 10;

    if (this.maxCatDisplay >= this.filteredTemplates.length) {
      this.viewAllCategories = false;
    }
  }

  getStyle(img) {
    return `background-image: url('/assets/thumbnail/${img}')`;
  }

  getList(desc) {
    return (desc && desc.replace(/[\"{}]/g, '').split('|')) || [];
  }

  updateTemplateDisplay(filter) {
    // this.maxCatDisplay = 4;
    this.selectedTemplateFilter = filter;
    this.viewAllCategories = this.maxCatDisplay < this.filteredTemplates.length;

    this.storeTemplate.patchValue('');
  }

  get canValidateDomain() {
    const subDom = this.subdomainName.valid || this.subdomainName.disabled;
    if (this.useCustomDomain.value) {
      return subDom && this.customDomain.valid;
    }

    return subDom;
  }

  scrollToTop(el) {
    el.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  private getPageOffsetTopOf(element) {
    let currentElement = element;
    let top = currentElement.offsetTop;

    while (currentElement.offsetParent) {
      currentElement = currentElement.offsetParent;
      top += currentElement.offsetTop;
    }

    return top;
  }

  getWindowScrollY() {
    return typeof window.scrollY === 'number'
      ? window.scrollY
      : document.documentElement.scrollTop;
  }

  openTemplate(template, state) {
    if (this.isMobileView) {
      if (state.canOpen) {
        window.open(template.store_preview_link || '#', '_blank');
      }
      state.canOpen = true;
    } else {
      window.open(template.store_preview_link || '#', '_blank');
    }
  }

  private doScroll() {
    if (this.footerContent) {
      let scrollY = this.getWindowScrollY();
      let showBackToTopThreshold = window.innerHeight;
      let windowBottom = scrollY + window.innerHeight;
      let absoluteThreshold = this.getPageOffsetTopOf(
        this.footerContent.nativeElement,
      );

      this.backToTopIsActive = scrollY >= showBackToTopThreshold;
      this.backToTopIsAbsolute = absoluteThreshold <= windowBottom;
    }

    if (!this.cdr['destroyed']) {
      this.cdr.detectChanges();
    }
  }

  get storeUrl() {
    const storeDomain = environment.storeSubdomain ?? 'estore5s.com';
    let url = this.subdomainName.value.trim();
    if (this.paidSelected) {
      url = this.customDomain.value;
    } else {
      url = `${url}.${storeDomain}`;
    }
    return `${url}`;
  }

  get form() {
    return this.storeForm;
  }

  get storeTemplate() {
    return this.form.get('template');
  }

  get storePlan() {
    return this.form.get('plan');
  }

  get email() {
    return this.form.get('email');
  }

  get name() {
    return this.form.get('name');
  }

  get country() {
    return this.form.get('country');
  }

  get language() {
    return this.form.get('language');
  }

  get useCustomDomain() {
    return this.form.get('useCustomDomain');
  }

  get subdomainName() {
    return this.form.get('subdomainName');
  }

  get customDomain() {
    return this.form.get('customDomain');
  }

  get filteredTemplates() {
    let filtered = [];
    const templates = this.store.selectSnapshot(StoreState.templates);
    if (templates.length) {
      filtered = templates.filter(
        (item) => item['store_price'] == (this.paidSelected ? 'paid' : 'free'),
      );
    }

    const filter = this.selectedTemplateFilter.value;
    // console.log(filtered)
    if (filter != 'All') {
      filtered = filtered.filter((item) => item.category == filter);
    }

    // this.viewAllCategories = this.maxCatDisplay < filtered.length;

    return filtered;
  }

  get filteredPlans() {
    const plans = this.store.selectSnapshot(StoreState.plans);
    if (plans.length) {
      if (this.paidSelected) {
        return plans.filter((item) => item['plan_price'] != 0);
      }
      return plans;
    }

    return [];
  }

  get currentYear() {
    return new Date().getFullYear();
  }
}
